import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Typography,
  FormControl,
  Select,
  CircularProgress,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ReplayIcon from "@mui/icons-material/Replay";
import BlockIcon from "@mui/icons-material/Block";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PrintIcon from "@mui/icons-material/Print";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PosLayout } from "../../../components/layouts/pos-layout/PosLayout";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { getUserNotesByIdAction } from "../../../redux/actions/customerAction";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { CustomerSliceAction } from "../../../redux/slice/customerSlice";
import { useUserNote } from "../../../providers/UserNoteProvider";
import { NoteOccasion } from "../../../typings/enum/noteOccasions";
import { PosInnerPageLayout } from "../../../components/layouts/pos-layout/PosInnerPageLayout";
import { CustomButton, CustomTextBox } from "../../../components/atoms";
import CustomerDetailSection from "../../../components/common/pos/customer-detail-section/customer-detail-section";
import OrderDetailSection from "../../../components/common/pos/order-detail-section/order-detail-section";
import CartSection from "../../../components/common/pos/cart-section/cart-section";
import CustomMarquee from "../../../components/common/pos/marquee/marquee";
import CustomerHistoryTable from "../../../components/common/pos/customer-history-table/customer-history-table";
import BoxStorageHistoryTable from "../../../components/common/pos/box-storage-history-table/box-storage-history-table";
import PickupDetailsSection from "../../../components/common/pos/pickup-details-section/pickup-details-section";
import SingleDetail, {
  ISingleDetailProps,
} from "../../../components/atoms/single-detail/single-detail";
import PickupModal from "./PickupModal";

import { Colors } from "../../../configs";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import { DiscountSliceAction } from "../../../redux/slice/discountSlice";
import { pxToRem, formatDate, formatCurrency } from "../../../util";
import {
  BillFilterDateType,
  BillPaymentStatus,
  BillPickupStatus,
} from "../../../typings/enum/paymentTypes";
import { DatePicker } from "../../../components/atoms/custom-date-time-picker/DateRangePicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VoidInvoiceModal from "./VoidInvoiceModal";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import useTagPrinter from "../../../hooks/useTagPrinter";
import OrderManifestReport, {
  IOrderManifestRow,
} from "./OrderManifestReport";
import { printPdfDocument } from "../../../util/pdf-functions";
import { IBillPaymentHistoryRequest } from "../../../typings/interfaces/requests/bill-payment-historyRequest";
import {
  getBillByIdAction,
  getBillPaymentHistoryAction,
} from "../../../redux/actions/cartAction";
import { IBillPaymentHistory } from "../../../typings/interfaces/responses/paymentResponse";
import { RefundModal } from "./RefundModal";
import NoteSection from "../../../components/common/pos/cart-section/note-section";
import TopRightButton from "../../../components/common/top-right-button/top-right-button";
import { getCustomerOutstandingAction } from "../../../redux/actions/customerInsightsAction";
import { getAllCustomerLatestBillDetailsAction } from "../../../redux/actions/customerLatestBillDetailsAction";
import BoxStorageModal from "./BoxStorageModal";
import RenewBoxStorageModal from "./RenewBoxStorageModal";
import AddCardWarningModal from "../../../components/pos/add-card-warning-modal/AddCardWarningModal";
import { AddBoxStorageBody, IBoxStorageHistoryRow } from "../../../typings";
import { getBoxStorageAction } from "../../../redux/actions/boxStorageAction";
import ManagerCodeModal from "../../../components/common/manager-code-modal/ManagerCodeModal";

export interface ICustomerHistoryPageProps {
  pickupMode?: boolean;
}

const CustomerHistoryPage: FC<ICustomerHistoryPageProps> = ({
  pickupMode = false,
}) => {
  const [isEditAccessModalOpen, setIsEditAccessModalOpen] = useState(false);
  const [isVoidAccessModalOpen, setIsVoidAccessModalOpen] = useState(false);
  const [voidApprovedManagerId, setVoidApprovedManagerId] = useState<
    number | undefined
  >(undefined);
  const [isRefundAccessModalOpen, setIsRefundAccessModalOpen] = useState(false);
  const [refundApprovedManagerId, setRefundApprovedManagerId] = useState<
    number | undefined
  >(undefined);
  const { user } = useAppSelector((state) => state.auth);

  const _handleEditInvoice = () => {
    if (customerId) {
      if (cartHistorySelectedRows.length > 1) {
        snackbar.showSnackbar(
          "You can only edit one invoice at a time",
          "error"
        );
        return;
      }
      navigate(
        `/pos/${customerId}/bills/${cartHistorySelectedRows[0].id}/edit`
      );
    } else {
      snackbar.showSnackbar("Customer not found", "error");
    }
  };

  const _handleOpenVoidInvoiceModal = () => {
    if (cartHistorySelectedRows.length > 1) {
      snackbar.showSnackbar("You can only void one invoice at a time", "error");
      return;
    }
    handleFetchBillPaymentHistory(() => setIsVoidModalOpen(true));
  };

  const _handleOpenRefundInvoiceModal = () => {
    if (cartHistorySelectedRows.length > 1) {
      snackbar.showSnackbar(
        "You can only refund one invoice at a time",
        "error"
      );
      return;
    }
    handleFetchBillPaymentHistory(() => setIsRefundModalOpen(true));
  };

  const details: ISingleDetailProps[] = [
    { label: "Total Invoice", value: 2 },
    { label: "Total Price", value: 5 },
    { label: "Retail Items", value: "$ 0.00" },
    { label: "Total", value: "$ 22.00" },
    { label: "Discounts", value: "$ 0.00" },
    { label: "Taxes", value: "$ 0.00" },
    { label: "Total Due", value: "$ 22.00" },
  ];

  const [filterExpand, setFilterExpand] = useState(pickupMode ? false : true);
  const { showNote } = useUserNote();
  const { cartMode } = useAppSelector((state) => state.cart);
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  // Tracks whether the modal should open with partial-pickup selection mode
  // pre-enabled (set by the "Partial Pickup" entry point below).
  const [partialPickupRequested, setPartialPickupRequested] = useState(false);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<
    string | undefined
  >(undefined);
  const [isReundModalOpen, setIsRefundModalOpen] = useState(false);
  const [voidTypeIndex, setVoidTypeIndex] = useState<number>(0);

  const [isAddCardWarningOpen, setIsAddCardWarningOpen] = useState(false);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    cartHistorySelectedRows,
    selectedSession,
    billPaymentDetailsLoading,
    billItemIdListLoading,
    billPaymentDetails,
    billById,
    refreshCustomerHistory,
    billItemIdList,
  } = useAppSelector((state) => state.cart);
  const { voidInvoiceLoading, voidInvoiceResponse, refundInvoiceResponse } =
    useAppSelector((state) => state.invoice);
  const {
    customerNotes,
    customer,
    customerOutstanding,
    customerLatestBillDetails,
  } = useAppSelector((state) => state.customer);
  const [activeHistoryTab, setActiveHistoryTab] = useState<
    "invoices" | "boxStorage"
  >("invoices");

  const params = useParams();
  const snackbar = useSnackbarContext();
  const customerId = params.id;
  const { printRackTag, isPrinting: isPrintingRackTag } = useTagPrinter();

  const changeCartMode = (mode: "check-in" | "pickup") => {
    if (cartMode !== mode) {
      dispatch(CartSliceAction.setCartMode(mode));
    }
    dispatch(CartSliceAction.clearSession());
    dispatch(DiscountSliceAction.resetDiscount());
  };

  // Runs once per page load / route change: sets the base cart mode and
  // clears any stale session state left over from a previous customer.
  useEffect(() => {
    changeCartMode(pickupMode ? "pickup" : "check-in");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, pickupMode]);

  // Keeps cartMode in sync with the Pickup modal without clearing the
  // session on every open/close — clearSession() resets paymentHalfDetails
  // to a new array reference, which re-triggers PickupModal's half-payment
  // effect and closes the modal right after it opens.
  useEffect(() => {
    const mode = isPickupModalOpen || pickupMode ? "pickup" : "check-in";
    if (cartMode !== mode) {
      dispatch(CartSliceAction.setCartMode(mode));
    }
  }, [isPickupModalOpen, pickupMode, cartMode, dispatch]);

  // const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs(new Date()));
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, endDate] = dateRange;
  const [selectedDateType, setSelectedDateType] = useState<BillFilterDateType>(
    BillFilterDateType.DATE_IN
  );

  const [paymentStatus, setPaymentStatus] = useState<BillPaymentStatus | "">(
    ""
  );
  const [pickingStatus, setPickingStatus] = useState<BillPickupStatus | "">("");
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [billIds, setBillIds] = useState([]);

  // Read directly off the selected row instead of a separate, unscoped bill
  // search — that search isn't filtered by customer and can miss this bill
  // entirely, leaving these permanently stale after a void/pickup change.
  const pickingFilterStatus = cartHistorySelectedRows[0]?.pickingFilterStatus;
  const voidStatus = cartHistorySelectedRows[0]?.isVoided;

  // "In Store" mirrors the status pill logic in customer-history-table-row.tsx:
  // not voided/refunded and not (partially) picked up.
  const isInStoreBill = (row: (typeof cartHistorySelectedRows)[number]) =>
    !row.isVoided &&
    !row.isRefunded &&
    row.pickingFilterStatus !== "PICKED_UP" &&
    row.pickingFilterStatus !== "PARTIALLY_PICKED_UP";

  const hasVoidedAndInStoreMixedSelection =
    cartHistorySelectedRows.some((row) => row.isVoided) &&
    cartHistorySelectedRows.some(isInStoreBill);

  // Edit/Void/Refund all operate on a single bill — _handleEditInvoice,
  // _handleOpenVoidInvoiceModal and _handleOpenRefundInvoiceModal each
  // still guard against multi-select too (in case selection changes while
  // one of their modals is open), but disabling the buttons up front
  // blocks the path instead of letting the user hit an error toast.
  const multipleBillsSelected = cartHistorySelectedRows.length > 1;

  // Edit Invoice used to block on ANY bill-level payment at all, which also
  // blocked the exact "partially picked up + partially paid" bills the
  // per-item locking in Edit Invoice (CartRow's isItemLocked) exists to
  // handle. That per-item lock only protects money it can attribute to a
  // specific item though - a payment recorded the old bill-level-only way
  // (no itemIds) can't be pinned to any one item, so editing must still be
  // blocked whenever such an "unattributed" payment exists. billItemIdList
  // is already fetched as soon as the row is checked (see
  // customer-history-table-row.tsx's selectRow), so it's available here.
  const selectedBillHasUnattributedPayment = (() => {
    const row = cartHistorySelectedRows[0];
    if (!row) return false;
    const billPaid = row.paidAmount ?? 0;
    if (billPaid <= 0) return false;
    const entry = billItemIdList.find((b) => b.billId === row.id);
    const itemScopedPaidTotal = (entry?.billItemIdList ?? []).reduce(
      (sum, item) => sum + (item.paidAmount ?? 0),
      0
    );
    return billPaid - itemScopedPaidTotal > 0.01;
  })();

  const handlePaymentStatusChange = (event: SelectChangeEvent<string>) => {
    setPaymentStatus(event.target.value as BillPaymentStatus | "");
  };

  const handlePickingStatusChange = (event: SelectChangeEvent<string>) => {
    setPickingStatus(event.target.value as BillPickupStatus | "");
  };

  const handleResetFilters = () => {
    setSelectedDateType(BillFilterDateType.DATE_IN);
    setDateRange([null, null]);
    setPaymentStatus("");
    setPickingStatus("");
    setSearchQuery(undefined);
  };

  // Stats row (height + the extra flex gap it introduces) eats into the
  // table's fixed height budget below, since it only renders for !pickupMode.
  const statsRowOverhead = 9.99;
  let tableContentHeight = pickupMode
    ? 21.92
    : 39.07 - statsRowOverhead;

  // Table wrapper grows to fill the space freed up when the filter row
  // collapses, so the table reaches down to the bottom section either way.
  const tableWrapperHeight = `${
    filterExpand ? tableContentHeight + 7.71 : tableContentHeight + 18.71
  }rem`;
  // Sticky header row (2.92857142857rem) + the grid gap below it (0.21rem)
  // is fixed overhead that doesn't scroll with the rows.
  const tableHeaderOverhead = 3.14;
  const tableRowsHeight = `${
    (filterExpand ? tableContentHeight + 7.71 : tableContentHeight + 18.71) -
    tableHeaderOverhead
  }rem`;

  useEffect(() => {
    if (customerId) {
      dispatch(getUserNotesByIdAction(customerId as string)).then(
        (res: any) => {
          showNote(NoteOccasion.ON_CHECKOUT, res.payload);
        }
      );
    }
    return () => {
      dispatch(CustomerSliceAction.clearCustomerNotes());
    };
  }, [customerId]);

  useEffect(() => {
    if (customerId) {
      dispatch(getCustomerOutstandingAction(Number(customerId)));
      dispatch(getAllCustomerLatestBillDetailsAction(Number(customerId)));
    }
  }, [customerId]);

  useEffect(() => {
    return () => {
      dispatch(CartSliceAction.clearSession());
      dispatch(DiscountSliceAction.resetDiscount());
      // cartHistorySelectedRows/billItemIdList are global cart-slice state,
      // not scoped to this page or this customer - clearSession() doesn't
      // touch them (same reasoning already applied on every successful
      // Void/Refund/Pickup below), so without this they silently carry a
      // stale bill selection into the next visit to this page, or into a
      // different customer's history entirely.
      dispatch(CartSliceAction.setCartHistorySelectedRows([]));
      dispatch(CartSliceAction.setBillItemIdList([]));
    };
  }, []);

  const totalBillValue = cartHistorySelectedRows.reduce(
    (total, bill) => total + (bill.billAmount || 0),
    0
  );
  const totalDiscountValue = cartHistorySelectedRows.reduce(
    (total, bill) => total + (bill.discountAmount || 0),
    0
  );
  const totalDue = cartHistorySelectedRows.reduce((total, bill) => {
    const due =
      (bill.billAmount || 0) -
      (bill.discountAmount || 0) -
      (bill.paidAmount || 0);
    return total + (due > 0 ? due : 0);
  }, 0);

  const openBalanceTotal = customerOutstanding?.outstandingAmount ?? 0;
  const openInvoiceCount = customerOutstanding?.openInvoiceCount ?? 0;
  const totalSelectedPieces = cartHistorySelectedRows.reduce(
    (total, bill) => total + (bill.totalPieceCount || 0),
    0
  );
  const lastVisitDate = customerLatestBillDetails?.createdDate ?? null;

  const getFormattedStatus = (value: string | "") => {
    if (value === "") return "All";
    return value
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  const fetchBillPaymentHistoryOnMount = () => {
    if (cartHistorySelectedRows.length > 0) {
      const req: IBillPaymentHistoryRequest = {
        paymentType: null,
        billId: cartHistorySelectedRows[0]?.id,
        customerId: null,
        paymentClearStatus: "CLEARED",
        startDate: null,
        endDate: null,
      };
      dispatch(getBillPaymentHistoryAction(req)).catch((err) => {
        snackbar.showSnackbar(err, "error");
      });
    }
  };

  useEffect(() => {
    if (voidInvoiceResponse) {
      dispatch(
        getBillByIdAction({ billId: String(cartHistorySelectedRows[0]?.id) })
      );
    }
  }, [voidInvoiceResponse]);

  useEffect(() => {
    if (refundInvoiceResponse) {
      dispatch(
        getBillByIdAction({ billId: String(cartHistorySelectedRows[0]?.id) })
      );
    }
  }, [refundInvoiceResponse]);

  useEffect(() => {
    if (cartHistorySelectedRows.length > 0) {
      fetchBillPaymentHistoryOnMount();
    }
  }, [cartHistorySelectedRows]);

  const handleFetchBillPaymentHistory = (onDone: () => void) => {
    if (cartHistorySelectedRows.length > 0) {
      const req: IBillPaymentHistoryRequest = {
        paymentType: null,
        billId: cartHistorySelectedRows[0]?.id,
        customerId: null,
        paymentClearStatus: "CLEARED",
        startDate: null,
        endDate: null,
      };
      dispatch(getBillPaymentHistoryAction(req))
        .then((res: any) => {
          if (res?.meta?.requestStatus === "rejected") {
            snackbar.showSnackbar("Failed to load payment history", "error");
            return;
          }
          onDone();
        })
        .catch((err) => {
          snackbar.showSnackbar(err, "error");
        });
    } else {
      snackbar.showSnackbar("Please select an invoice", "error");
    }
  };

  const handleEditInvoice = () => {
    setIsEditAccessModalOpen(true);
  };

  const handleOpenVoidInvoiceModal = () => {
    setIsVoidAccessModalOpen(true);
  };

  const handleOpenRefundInvoiceModal = () => {
    setIsRefundAccessModalOpen(true);
  };

  const getBillDepartment = (bill: (typeof cartHistorySelectedRows)[number]) => {
    try {
      const session = bill.billString ? JSON.parse(bill.billString) : null;
      return (
        session?.cartItems?.[0]?.serviceType?.categoryName ?? "N/A"
      );
    } catch {
      return "N/A";
    }
  };

  const handlePrintManifest = async () => {
    if (cartHistorySelectedRows.length === 0) {
      snackbar.showSnackbar(
        "Select at least one invoice to print a manifest.",
        "error"
      );
      return;
    }

    const rows: IOrderManifestRow[] = cartHistorySelectedRows.map((bill) => ({
      invoiceNumber: bill.billId,
      department: getBillDepartment(bill),
      pieceCount: bill.totalPieceCount ?? 0,
    }));

    const customerName = customer
      ? `${customer.firstName} ${customer.lastName}`.trim()
      : "";

    try {
      await printPdfDocument(
        <OrderManifestReport
          customerName={customerName}
          customerId={customer?.id != null ? String(customer.id) : (customerId as string) ?? ""}
          rows={rows}
        />
      );
    } catch (err) {
      snackbar.showSnackbar("Failed to generate the manifest report.", "error");
    }
  };

  const handlePrintRackLocation = async () => {
    if (cartHistorySelectedRows.length === 0) {
      snackbar.showSnackbar(
        "Select at least one invoice to print rack location.",
        "error"
      );
      return;
    }

    const customerName = customer
      ? `${customer.firstName} ${customer.lastName}`.trim()
      : "";

    let anyFailed = false;
    for (const bill of cartHistorySelectedRows) {
      const ok = await printRackTag({
        invoice: bill.billId,
        customer: customerName,
        rackLocation: bill.rackNumber || "N/A",
        pieces: bill.totalPieceCount ?? 0,
        dropOff: formatDate(bill.createdTime),
        dueDate: bill.dueDate ? formatDate(bill.dueDate) : undefined,
      });
      if (!ok) anyFailed = true;
    }

    if (anyFailed) {
      snackbar.showSnackbar(
        "Some rack tags failed to print. Check the tag printer in Presto Tray.",
        "error"
      );
    } else {
      snackbar.showSnackbar("Rack location tag(s) sent to the printer.", "success");
    }
  };

  const [isBoxStorageOpen, setIsBoxStorageOpen] = useState(false);
  const selectedBillIds = (cartHistorySelectedRows ?? []).map((r) => r.id);

  const [isRenewStorageOpen, setIsRenewStorageOpen] = useState(false);
  const [selectedStorageBoxes, setSelectedStorageBoxes] = useState<
    IBoxStorageHistoryRow[]
  >([]);
  const [storageRefreshKey, setStorageRefreshKey] = useState(0);

  // a pickup/void/refund on a box-stored invoice (PickupModal etc.) flips this
  // flag for the invoices tab table; also refresh the box storage tab table so
  // its pickup status/dates stay in sync regardless of which tab is active.
  useEffect(() => {
    if (refreshCustomerHistory) {
      setStorageRefreshKey((key) => key + 1);
    }
  }, [refreshCustomerHistory]);

  const [selectedBillsBoxInfo, setSelectedBillsBoxInfo] = useState<
    Record<number, { hasBox: boolean; storageId?: number }>
  >({});
  const [assignabilityLoading, setAssignabilityLoading] = useState(false);
  type GetBoxStorageResp = { body?: AddBoxStorageBody[] | null };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (selectedBillIds.length === 0) {
        setSelectedBillsBoxInfo({});
        return;
      }

      setAssignabilityLoading(true);
      try {
        const results: Array<{ id: number; res: GetBoxStorageResp }> =
          await Promise.all(
            selectedBillIds.map((id) =>
              dispatch(getBoxStorageAction(id))
                .unwrap()
                .then((res: GetBoxStorageResp) => ({ id, res }))
            )
          );

        if (cancelled) return;

        const map: Record<number, { hasBox: boolean; storageId?: number }> = {};

        results.forEach(
          ({ id, res }: { id: number; res: GetBoxStorageResp }) => {
            const items = Array.isArray(res?.body)
              ? (res.body as AddBoxStorageBody[])
              : [];
            const rec =
              items.find(
                (r: AddBoxStorageBody) =>
                  Array.isArray(r.billIds) && r.billIds.includes(id)
              ) ?? null;

            map[id] = rec
              ? { hasBox: true, storageId: (rec as any).id }
              : { hasBox: false };
          }
        );

        setSelectedBillsBoxInfo(map);
      } catch {
        const map: Record<number, { hasBox: boolean }> = {};
        selectedBillIds.forEach((id) => (map[id] = { hasBox: true }));
        setSelectedBillsBoxInfo(map);
      } finally {
        if (!cancelled) setAssignabilityLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [dispatch, selectedBillIds.join(",")]);

  const noneSelected = selectedBillIds.length === 0;
  const singleSelected = selectedBillIds.length === 1;

  const anyBoxed = selectedBillIds.some(
    (id) => selectedBillsBoxInfo[id]?.hasBox === true
  );
  const allUnboxed =
    selectedBillIds.length > 0 &&
    selectedBillIds.every(
      (id) =>
        selectedBillsBoxInfo[id] && selectedBillsBoxInfo[id].hasBox === false
    );

  const hasVoidedOrPickedUpSelection = cartHistorySelectedRows.some(
    (row) => row.isVoided || row.pickingFilterStatus === "PICKED_UP"
  );

  const disableAssign =
    assignabilityLoading ||
    noneSelected ||
    (!singleSelected && !allUnboxed) ||
    hasVoidedOrPickedUpSelection;

  const allSelectedStorageBoxesPickedUp =
    selectedStorageBoxes.length > 0 &&
    selectedStorageBoxes.every((box) => box.status === "PICKED_UP");

  const hasMixedPickupStorageSelection =
    selectedStorageBoxes.some((box) => box.status === "PICKED_UP") &&
    selectedStorageBoxes.some((box) => box.status !== "PICKED_UP");

  const handleOpenPartialPickup = () => {
    setPartialPickupRequested(true);
    setIsPickupModalOpen(true);
  };

  const pickupActionsDisabled =
    cartHistorySelectedRows.length === 0 ||
    billItemIdListLoading ||
    hasMixedPickupStorageSelection;

  const allSelectedAlreadyPickedUp =
    cartHistorySelectedRows.length > 0 &&
    cartHistorySelectedRows.every(
      (row) => row.pickingFilterStatus === BillPickupStatus.PICKED_UP
    );

  const rightBottomButtons = [
    { label: "Pre Pay - Not Pickup", btnColor: Colors.SECONDARY },
    { label: "Pay and Pickup", btnColor: Colors.PRIMARY },
    { label: "Print Invoice Location", btnColor: Colors.MAIN_GRAY },
    { label: "Add Retail Item", btnColor: Colors.MAIN_GRAY },
    {
      label: "Partial Pickup",
      btnColor: Colors.MAIN_GRAY,
      onClick: handleOpenPartialPickup,
      disabled: pickupActionsDisabled,
    },
    { label: "-", btnColor: Colors.MAIN_GRAY },
  ];

  return (
    <PosLayout>
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "100%",
            gap: "0.57rem",
          }}
        >
          <Box
            sx={{
              flex: 4,
              display: "flex",
              flexDirection: "column",
              gap: "0.57rem",
              height: "100%",
              maxHeight: "100% !important",
            }}
          >
            <CustomerDetailSection
              showAnalysisButton={true}
              showEditButton={true}
              showHistoryButton={false}
            />
            <CartSection mode="pickup" onSelect={() => {}} />
            <NoteSection />
            <CustomMarquee
              text={
                customerNotes == null || customerNotes.length === 0
                  ? ""
                  : customerNotes
                      .filter((note) =>
                        note.noteShowOccasion?.includes(
                          NoteOccasion.ON_CHECKOUT
                        )
                      )
                      .map((note) => note.note)
                      .join(", ")
              }
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "1.07rem",
                flexShrink: 0,
                marginTop: "auto",
                alignItems: "flex-end",
              }}
            >
              <OrderDetailSection />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.71rem",
                }}
              >
                <Box
                  sx={{
                    border: `${pxToRem(1)} solid ${Colors.ACCENT_GRAY}`,
                    borderRadius: pxToRem(18),
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      background: Colors.WHITE,
                      padding: pxToRem(12),
                    }}
                  >
                    {activeHistoryTab === "invoices" ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: pxToRem(8),
                    }}
                  >
                    {[
                      {
                        icon: billPaymentDetailsLoading
                          ? CircularProgress
                          : EditNoteIcon,
                        label: "Edit Invoice",
                        onClick: handleEditInvoice,
                        disabled:
                          cartHistorySelectedRows.length === 0 ||
                          multipleBillsSelected ||
                          selectedBillHasUnattributedPayment ||
                          billPaymentDetailsLoading ||
                          voidStatus ||
                          hasVoidedAndInStoreMixedSelection,
                      },
                      {
                        icon: billPaymentDetailsLoading
                          ? CircularProgress
                          : BlockIcon,
                        label: "Void Invoice",
                        onClick: handleOpenVoidInvoiceModal,
                        disabled:
                          cartHistorySelectedRows.length === 0 ||
                          multipleBillsSelected ||
                          billPaymentDetailsLoading ||
                          voidInvoiceLoading ||
                          voidStatus ||
                          pickingFilterStatus == "PICKED_UP" ||
                          hasVoidedAndInStoreMixedSelection,
                      },
                      {
                        icon: billPaymentDetailsLoading
                          ? CircularProgress
                          : ReplayIcon,
                        label: "Refund",
                        onClick: handleOpenRefundInvoiceModal,
                        disabled:
                          cartHistorySelectedRows.length === 0 ||
                          multipleBillsSelected ||
                          billPaymentDetailsLoading ||
                          (billPaymentDetails?.payments ?? []).length === 0 ||
                          (billPaymentDetails?.totalAmount ?? 0) === 0 ||
                          billById?.billStatus == "REFUNDED" ||
                          hasVoidedAndInStoreMixedSelection,
                      },
                    ].map((item, index) => (
                      <CustomButton
                        key={index}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: pxToRem(6),
                          background: Colors.WHITE,
                          border: `${pxToRem(1)} solid ${
                            item.disabled
                              ? Colors.ACCENT_GRAY
                              : `${Colors.PRIMARY}26`
                          }`,
                          borderRadius: pxToRem(14),
                          width: pxToRem(131),
                          minWidth: pxToRem(131),
                          maxWidth: pxToRem(131),
                          height: pxToRem(100),
                          boxShadow: item.disabled
                            ? "none"
                            : "0 1px 2px rgba(0,0,0,0.04)",
                          transition: "background 0.15s ease, border-color 0.15s ease",
                          "&:hover": {
                            background: item.disabled
                              ? Colors.WHITE
                              : `${Colors.PRIMARY}0D`,
                            border: `${pxToRem(1)} solid ${
                              item.disabled ? Colors.ACCENT_GRAY : Colors.PRIMARY
                            }`,
                          },
                          "&.Mui-disabled": {
                            background: Colors.WHITE,
                          },
                        }}
                        test-name={item.label}
                        disabled={item.disabled}
                        onClick={item.onClick}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: pxToRem(40),
                            height: pxToRem(40),
                            borderRadius: "50%",
                            background: item.disabled
                              ? Colors.BACKSHADE_GRAY
                              : `${Colors.PRIMARY}1A`,
                          }}
                        >
                          <item.icon
                            sx={{
                              fontSize: pxToRem(22),
                              color: item.disabled
                                ? Colors.ACCENT_GRAY_DISABLED
                                : Colors.PRIMARY,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            color: item.disabled
                              ? Colors.ACCENT_GRAY_DISABLED
                              : Colors.MAIN_GRAY,
                            fontSize: "var(--fs-2xs)",
                            fontWeight: "bold",
                            textAlign: "center",
                            lineHeight: 1.2,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </CustomButton>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: pxToRem(8),
                    }}
                  >
                    {[
                      {
                        icon: AddBoxIcon,
                        label: "Assign Box",
                        onClick: () => setIsBoxStorageOpen(true),
                        disabled: disableAssign || selectedStorageBoxes.length > 0,
                      },
                      {
                        icon: AssignmentIcon,
                        label: "Storage List",
                        onClick: () => setActiveHistoryTab("boxStorage"),
                        disabled:
                          allSelectedStorageBoxesPickedUp ||
                          hasMixedPickupStorageSelection,
                      },
                      {
                        icon: AutorenewIcon,
                        label: "Renew Storage",
                        onClick: () => setIsRenewStorageOpen(true),
                        disabled:
                          selectedStorageBoxes.length === 0 ||
                          allSelectedStorageBoxesPickedUp ||
                          hasMixedPickupStorageSelection,
                      },
                    ].map((item, index) => (
                      <CustomButton
                        key={index}
                        disabled={item.disabled}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: pxToRem(6),
                          background: Colors.WHITE,
                          border: `${pxToRem(1)} solid ${
                            item.disabled
                              ? Colors.ACCENT_GRAY
                              : `${Colors.PRIMARY}26`
                          }`,
                          borderRadius: pxToRem(14),
                          width: pxToRem(131),
                          minWidth: pxToRem(131),
                          maxWidth: pxToRem(131),
                          height: pxToRem(100),
                          boxShadow: item.disabled
                            ? "none"
                            : "0 1px 2px rgba(0,0,0,0.04)",
                          transition: "background 0.15s ease, border-color 0.15s ease",
                          "&:hover": {
                            background: item.disabled
                              ? Colors.WHITE
                              : `${Colors.PRIMARY}0D`,
                            border: `${pxToRem(1)} solid ${
                              item.disabled ? Colors.ACCENT_GRAY : Colors.PRIMARY
                            }`,
                          },
                          "&.Mui-disabled": {
                            background: Colors.WHITE,
                          },
                        }}
                        onClick={item.onClick}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: pxToRem(40),
                            height: pxToRem(40),
                            borderRadius: "50%",
                            background: item.disabled
                              ? Colors.BACKSHADE_GRAY
                              : `${Colors.PRIMARY}1A`,
                          }}
                        >
                          <item.icon
                            sx={{
                              fontSize: pxToRem(22),
                              color: item.disabled
                                ? Colors.ACCENT_GRAY_DISABLED
                                : Colors.PRIMARY,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            color: item.disabled
                              ? Colors.ACCENT_GRAY_DISABLED
                              : Colors.MAIN_GRAY,
                            fontSize: "var(--fs-2xs)",
                            fontWeight: "bold",
                            textAlign: "center",
                            lineHeight: 1.2,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </CustomButton>
                    ))}
                  </Box>
                    )}
                  </Box>
                </Box>

                <CustomButton
                  name="pickup-button"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: pxToRem(12),
                    width: "100%",
                    height: pxToRem(76),
                    borderRadius: pxToRem(14),
                    background: Colors.PRIMARY,
                    textTransform: "none",
                    flexGrow: 1,
                    boxShadow: "0 4px 10px rgba(1,135,82,0.25)",
                    transition: "background 0.15s ease, box-shadow 0.15s ease",
                    "&:hover": {
                      background: Colors.PRIMARY_DARK,
                      boxShadow: "0 6px 14px rgba(1,135,82,0.3)",
                    },
                    "&.Mui-disabled": {
                      background: Colors.BACKSHADE_GRAY,
                      boxShadow: "none",
                    },
                  }}
                  onClick={() => {
                    setPartialPickupRequested(false);
                    setIsPickupModalOpen(true);
                  }}
                  disabled={
                    cartHistorySelectedRows.length === 0 ||
                    billItemIdListLoading ||
                    hasMixedPickupStorageSelection
                  }
                >
                  {billItemIdListLoading ? (
                    <CircularProgress
                      sx={{ color: Colors.ACCENT_GRAY_DISABLED }}
                    />
                  ) : (
                    <>
                      <ShoppingBagOutlinedIcon
                        sx={{
                          fontSize: pxToRem(31),
                          color:
                            cartHistorySelectedRows.length === 0 ||
                            hasMixedPickupStorageSelection
                              ? Colors.ACCENT_GRAY_DISABLED
                              : Colors.WHITE,
                        }}
                      />
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          sx={{
                            fontSize: "var(--fs-3xl)",
                            fontWeight: "bold",
                            lineHeight: 1.1,
                            color:
                              cartHistorySelectedRows.length === 0 ||
                              hasMixedPickupStorageSelection
                                ? Colors.ACCENT_GRAY_DISABLED
                                : Colors.WHITE,
                          }}
                        >
                          {allSelectedAlreadyPickedUp ? "Pay" : "Pickup"}
                        </Typography>
                      </Box>
                    </>
                  )}
                </CustomButton>
              </Box>
              <BoxStorageModal
                open={isBoxStorageOpen}
                onClose={() => setIsBoxStorageOpen(false)}
                billIds={(cartHistorySelectedRows ?? []).map((r) => r.id)}
                onAddSuccess={() => {
                  setIsBoxStorageOpen(false);
                  setIsAddCardWarningOpen(true);
                  setStorageRefreshKey((key) => key + 1);
                }}
              />
              <AddCardWarningModal
                open={isAddCardWarningOpen}
                onClose={() => setIsAddCardWarningOpen(false)}
              />
              <RenewBoxStorageModal
                open={isRenewStorageOpen}
                onClose={() => setIsRenewStorageOpen(false)}
                boxes={selectedStorageBoxes}
                onRenewSuccess={() => setStorageRefreshKey((key) => key + 1)}
              />
            </Box>
          </Box>
          <Box sx={{ width: "0.14rem", background: Colors.ACCENT_GRAY }}></Box>
          <Box
            sx={{
              flex: 5,
              display: "flex",
              flexDirection: "column",
              gap: "1.42rem",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: pxToRem(24),
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "2.57rem", color: "#4D4D4D" }}>
                {pickupMode ? "Pickup" : "Customer History"}
              </Typography>
              {!pickupMode && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: pxToRem(4),
                    background: Colors.BACKSHADE_GRAY,
                    padding: pxToRem(6),
                    borderRadius: pxToRem(14),
                    border: `${pxToRem(1)} solid ${Colors.ACCENT_GRAY}`,
                  }}
                >
                  <CustomButton
                    name="invoices-tab-button"
                    sx={{
                      background:
                        activeHistoryTab === "invoices"
                          ? Colors.PRIMARY
                          : "transparent",
                      color:
                        activeHistoryTab === "invoices"
                          ? Colors.WHITE
                          : Colors.PRIMARY,
                      borderRadius: pxToRem(10),
                      fontSize: "var(--fs-base)",
                      height: pxToRem(40),
                      paddingX: pxToRem(20),
                      fontWeight: "bold",
                      boxShadow:
                        activeHistoryTab === "invoices"
                          ? "0 1px 2px rgba(0,0,0,0.12)"
                          : "none",
                      transition: "background 0.15s ease",
                      "&:hover": {
                        background:
                          activeHistoryTab === "invoices"
                            ? Colors.PRIMARY
                            : Colors.WHITE,
                      },
                    }}
                    onClick={() => setActiveHistoryTab("invoices")}
                  >
                    Invoices
                  </CustomButton>
                  <CustomButton
                    name="box-storage-tab-button"
                    sx={{
                      background:
                        activeHistoryTab === "boxStorage"
                          ? Colors.PRIMARY
                          : "transparent",
                      color:
                        activeHistoryTab === "boxStorage"
                          ? Colors.WHITE
                          : Colors.PRIMARY,
                      borderRadius: pxToRem(10),
                      fontSize: "var(--fs-base)",
                      height: pxToRem(40),
                      paddingX: pxToRem(20),
                      fontWeight: "bold",
                      boxShadow:
                        activeHistoryTab === "boxStorage"
                          ? "0 1px 2px rgba(0,0,0,0.12)"
                          : "none",
                      transition: "background 0.15s ease",
                      "&:hover": {
                        background:
                          activeHistoryTab === "boxStorage"
                            ? Colors.PRIMARY
                            : Colors.WHITE,
                      },
                    }}
                    onClick={() => setActiveHistoryTab("boxStorage")}
                  >
                    Box Storage
                  </CustomButton>
                </Box>
              )}
            </Box>

            {!pickupMode && (
              <Box
                sx={{
                  width: "73.071rem",
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: pxToRem(16),
                  flexShrink: 0,
                }}
              >
                {[
                  {
                    label: "Open Balance",
                    value: formatCurrency({
                      amount: openBalanceTotal,
                      prefix: "$",
                    }),
                    color: Colors.BROWN,
                    icon: LockOutlinedIcon,
                  },
                  {
                    label: "Selected Invoices",
                    value: cartHistorySelectedRows.length,
                    color: Colors.SECONDARY,
                    icon: ReceiptLongIcon,
                  },
                  {
                    label: "Total Pieces",
                    value: totalSelectedPieces,
                    color: Colors.PRIMARY,
                    icon: CheckCircleOutlineIcon,
                  },
                  {
                    label: "Last Visit",
                    value: lastVisitDate ? formatDate(lastVisitDate) : "N/A",
                    color: Colors.SECONDARY,
                    icon: CalendarTodayIcon,
                  },
                ].map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      height: pxToRem(120),
                      boxSizing: "border-box",
                      border: `${pxToRem(1)} solid ${Colors.ACCENT_GRAY}`,
                      borderRadius: pxToRem(16),
                      background: Colors.WHITE,
                      padding: pxToRem(16),
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: pxToRem(12),
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "var(--fs-2xs)",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            textTransform: "uppercase",
                            letterSpacing: pxToRem(0.4),
                            color: `${Colors.MAIN_GRAY}99`,
                          }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          sx={{
                            mt: pxToRem(4),
                            fontSize: "var(--fs-xl)",
                            fontWeight: "bold",
                            lineHeight: 1.2,
                            color: Colors.MAIN_GRAY,
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: pxToRem(44),
                          height: pxToRem(44),
                          borderRadius: pxToRem(14),
                          background: stat.color,
                          flexShrink: 0,
                        }}
                      >
                        <stat.icon
                          sx={{ fontSize: pxToRem(20), color: Colors.WHITE }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {!pickupMode && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "1.42rem",
                  overflow: "hidden !important",
                  height: filterExpand ? "8.07142857143rem" : "0rem",
                  marginBottom: filterExpand ? "0rem" : "-1.42rem",
                  transition: "all 0.35s",
                  background: Colors.BACKGROUND_GRAY,
                  width: "73.071rem",
                  flexShrink: 0,
                  paddingX: "1.32rem",
                  borderRadius: "0.78rem",
                  justifyContent: "space-between",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      width: "33rem",
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Buttons Section */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        marginBottom: pxToRem(8),
                        height: "2.21428571429rem",
                        border: `${pxToRem(1)} solid ${Colors.SECONDARY}`,
                        borderRadius: pxToRem(10),
                        overflow: "hidden",
                      }}
                    >
                      {Object.values(BillFilterDateType).map(
                        (dateType, index) => (
                          <Button
                            test-name={dateType}
                            key={dateType}
                            onClick={() => {
                              setSelectedDateType(dateType);
                            }}
                            sx={{
                              flex: 1,
                              mx: 0,
                              height: "100%",
                              minHeight: 0,
                              minWidth: 0,
                              paddingY: pxToRem(3),
                              paddingX: pxToRem(8),
                              boxSizing: "border-box",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                              bgcolor:
                                selectedDateType === dateType
                                  ? Colors.SECONDARY
                                  : Colors.WHITE,
                              color:
                                selectedDateType === dateType
                                  ? Colors.WHITE
                                  : Colors.SECONDARY,
                              borderRight:
                                index === 3
                                  ? "none"
                                  : `${pxToRem(1)} solid ${Colors.SECONDARY}`,
                              borderRadius: 0,
                              fontWeight: 600,
                              fontSize: "var(--fs-2xs)",
                              textTransform: "none",
                              "&:hover": {
                                bgcolor:
                                  selectedDateType === dateType
                                    ? Colors.SECONDARY
                                    : `${Colors.SECONDARY}0D`,
                                color:
                                  selectedDateType === dateType
                                    ? Colors.WHITE
                                    : Colors.SECONDARY,
                              },
                            }}
                          >
                            {dateType
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase()
                              )
                              .join(" ")}
                          </Button>
                        )
                      )}
                    </Box>

                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: pxToRem(10),
                        border: `${pxToRem(1)} solid ${Colors.ACCENT_GRAY}`,
                        height: "2.21428571429rem",
                        bgcolor: Colors.WHITE,
                      }}
                    >
                      <DatePicker
                        type="range"
                        value={
                          startDate || endDate
                            ? {
                                from: startDate ?? undefined,
                                to: endDate ?? undefined,
                              }
                            : undefined
                        }
                        onChange={(val) => {
                          if (
                            val &&
                            typeof val === "object" &&
                            "from" in val &&
                            "to" in val
                          ) {
                            setDateRange([val.from ?? null, val.to ?? null]);
                          }
                        }}
                        minDate={new Date(2023, 0, 1)}
                        maxDate={new Date(2025, 11, 31)}
                        compact={true}
                        className="text-base"
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>

                {/* Right Section: Form fields */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: { lg: "1rem", md: "0.7rem", sm: "0.5rem" },
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                      width: {
                        lg: window.innerHeight <= 680 ? "8rem" : "10rem",
                        md: "9rem",
                        sm: "8rem",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        mb: "0.5rem",
                        fontSize: "var(--fs-2xs)",
                        fontWeight: 700,
                        color: Colors.MAIN_GRAY,
                      }}
                    >
                      Payment Status
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        test-name="test-name"
                        value={paymentStatus}
                        onChange={handlePaymentStatusChange}
                        displayEmpty
                        renderValue={(selected) => getFormattedStatus(selected)}
                        sx={{
                          height: "2.21428571429rem",
                          bgcolor: "white",
                          fontSize:
                            window.innerWidth <= 1290 ? "0.8rem" : "1rem",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.ACCENT_GRAY,
                            borderRadius: pxToRem(10),
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.SECONDARY,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.SECONDARY,
                            borderWidth: pxToRem(1.5),
                          },
                        }}
                      >
                        <MenuItem test-name="all" value="">
                          All
                        </MenuItem>
                        {Object.values(BillPaymentStatus).map(
                          (status, index) => (
                            <MenuItem
                              test-name={status}
                              key={index}
                              value={status}
                            >
                              {getFormattedStatus(status)}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                      width: {
                        lg: window.innerHeight <= 680 ? "8rem" : "10rem",
                        md: "9rem",
                        sm: "8rem",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        mb: "0.5rem",
                        fontSize: "var(--fs-2xs)",
                        fontWeight: 700,
                        color: Colors.MAIN_GRAY,
                      }}
                    >
                      Picking Status
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        test-name="picking-status"
                        value={pickingStatus}
                        onChange={handlePickingStatusChange}
                        displayEmpty
                        renderValue={(selected) => getFormattedStatus(selected)}
                        sx={{
                          height: "2.21428571429rem",
                          bgcolor: "white",
                          fontSize:
                            window.innerWidth <= 1290 ? "0.8rem" : "1rem",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.ACCENT_GRAY,
                            borderRadius: pxToRem(10),
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.SECONDARY,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.SECONDARY,
                            borderWidth: pxToRem(1.5),
                          },
                        }}
                      >
                        <MenuItem test-name="all" value="">
                          All
                        </MenuItem>
                        {Object.values(BillPickupStatus).map(
                          (status, index) => (
                            <MenuItem
                              test-name={status}
                              key={index}
                              value={status}
                            >
                              {getFormattedStatus(status)}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                      width: {
                        lg: window.innerHeight <= 680 ? "9.5rem" : "10rem",
                        md: "9rem",
                        sm: "8rem",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        mb: "0.5rem",
                        fontSize: "var(--fs-2xs)",
                        fontWeight: 700,
                        color: Colors.MAIN_GRAY,
                      }}
                    >
                      Invoice No
                    </Typography>
                    <CustomTextBox
                      variant="outlined"
                      size="small"
                      placeholder="Search"
                      fullWidth
                      test-name="invoice search"
                      value={searchQuery}
                      onChange={(e) => {
                        if (e.target.value === "") {
                          setSearchQuery(undefined);
                        } else {
                          setSearchQuery(e.target.value);
                        }
                      }}
                      inputProps={{
                        style: {
                          fontSize:
                            window.innerWidth <= 1290 ? "0.8rem" : "1rem",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        sx: {
                          height: "2.21428571429rem",
                          bgcolor: "white",
                          borderRadius: pxToRem(10),
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.ACCENT_GRAY,
                            borderRadius: pxToRem(10),
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.SECONDARY,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: Colors.SECONDARY,
                            borderWidth: pxToRem(1.5),
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" justifyContent="flex-end">
                    <CustomButton
                      name="reset-filters"
                      onClick={handleResetFilters}
                      title="Clear filters"
                      aria-label="Clear filters"
                      sx={{
                        width: "2.21428571429rem",
                        height: "2.21428571429rem",
                        minWidth: "2.21428571429rem",
                        padding: 0,
                        borderRadius: pxToRem(10),
                        background: Colors.SECONDARY,
                        boxShadow: "0 2px 6px rgba(0,121,194,0.3)",
                        "&:hover": {
                          background: Colors.SECONDARY,
                          opacity: 0.9,
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <RestartAltIcon sx={{ fontSize: "1.1rem" }} />
                    </CustomButton>
                  </Box>
                </Box>
              </Box>
            )}

            {activeHistoryTab === "invoices" && (
              <Box
                sx={{
                  width: "73.07rem",
                  height: tableWrapperHeight,
                  background: "transparent",
                  borderRadius: "0.78rem",
                  transition: "all 0.35s",
                }}
              >
                <CustomerHistoryTable
                  height={tableWrapperHeight}
                  contentHeight={tableRowsHeight}
                  paymentFilterStatus={
                    paymentStatus === "" ? null : paymentStatus
                  }
                  pickingFilterStatus={
                    pickingStatus === "" ? null : pickingStatus
                  }
                  filterDateType={selectedDateType}
                  startDate={startDate?.toISOString()}
                  endDate={endDate?.toISOString()}
                  searchQuery={debouncedSearchQuery}
                />
              </Box>
            )}

            {activeHistoryTab === "boxStorage" && !pickupMode && (
              <Box
                sx={{
                  width: "73.07rem",
                  height: tableWrapperHeight,
                  transition: "all 0.35s",
                }}
              >
                <BoxStorageHistoryTable
                  customerId={customerId}
                  height={tableWrapperHeight}
                  contentHeight={tableRowsHeight}
                  onSelectionChange={setSelectedStorageBoxes}
                  refreshKey={storageRefreshKey}
                />
              </Box>
            )}

            {/* bottom section for customer history */}
            {!pickupMode && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1.8rem",
                  marginTop: "auto",
                  width: "73.07rem",
                }}
              >
                {/* Left Section - Details */}
                <Box
                  sx={{
                    borderRadius: "1.1rem",
                    padding: "1.32rem 1.64285714rem",
                    background: Colors.WHITE,
                    border: `1px solid ${Colors.ACCENT_GRAY}`,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    width: "36rem",
                  }}
                >
                  <PickupDetailsSection
                    totalInvoices={cartHistorySelectedRows.length}
                    totalPrice={totalBillValue}
                    totalDiscounts={totalDiscountValue}
                    totalDue={totalDue}
                  />
                </Box>

                {/* Right Section - Two Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "0.9rem",
                  }}
                >
                  <CustomButton
                    onClick={handlePrintManifest}
                    disabled={cartHistorySelectedRows.length === 0}
                    sx={{
                      width: "17.3rem",
                      height: pxToRem(76),
                      borderRadius: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.6rem",
                      color: Colors.WHITE,
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      background: Colors.PRIMARY,
                      textTransform: "capitalize",
                      boxShadow: "0 4px 10px rgba(1,135,82,0.25)",
                      "&:hover": {
                        background: Colors.PRIMARY_DARK,
                      },
                      "&.Mui-disabled": {
                        background: Colors.BACKSHADE_GRAY,
                        color: Colors.ACCENT_GRAY_DISABLED,
                        boxShadow: "none",
                      },
                    }}
                  >
                    <PrintIcon sx={{ fontSize: "1.4rem" }} />
                    Print Manifest
                  </CustomButton>
                  <CustomButton
                    onClick={handlePrintRackLocation}
                    disabled={
                      cartHistorySelectedRows.length === 0 || isPrintingRackTag
                    }
                    sx={{
                      width: "17.3rem",
                      height: pxToRem(76),
                      borderRadius: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.6rem",
                      color: Colors.WHITE,
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      background: Colors.PRIMARY,
                      textTransform: "capitalize",
                      boxShadow: "0 4px 10px rgba(1,135,82,0.25)",
                      "&:hover": {
                        background: Colors.PRIMARY_DARK,
                      },
                      "&.Mui-disabled": {
                        background: Colors.BACKSHADE_GRAY,
                        color: Colors.ACCENT_GRAY_DISABLED,
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isPrintingRackTag ? (
                      <CircularProgress size={20} sx={{ color: Colors.WHITE }} />
                    ) : (
                      <WarehouseIcon sx={{ fontSize: "1.4rem" }} />
                    )}
                    Print Rack Location
                  </CustomButton>
                </Box>
              </Box>
            )}

            {/* bottom serction for pickup */}
            {pickupMode && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "1.42857143rem",
                }}
              >
                {/* Left Section - Details */}
                <Box
                  sx={{
                    borderRadius: "0.71rem",
                    padding: "1.64285714rem",
                    background: Colors.BACKGROUND_GRAY,
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.857rem",
                    width: "25.5rem",
                  }}
                >
                  {details.map((detail, index) => (
                    <SingleDetail
                      key={index}
                      label={detail.label}
                      value={detail.value}
                      color={Colors.PRIMARY}
                    />
                  ))}
                </Box>

                {/* Right Section - Buttons */}
                <Box
                  sx={{
                    width: "44.07142857142857rem",
                    borderRadius: "0.71rem",
                    padding: "1.9rem",
                    paddingTop: "3.8rem",
                    background: Colors.BACKGROUND_GRAY,
                    flex: 9,
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignContent: "flex-start",
                    justifyContent: "flex-start",
                    gap: "1rem",
                  }}
                >
                  {rightBottomButtons.map((rightBottomButton, index) => (
                    <CustomButton
                      key={index}
                      onClick={rightBottomButton.onClick}
                      disabled={rightBottomButton.disabled}
                      sx={{
                        width: "20.56857142857143rem",
                        height: "4.686428571428571rem",
                        borderRadius: "0.71rem",
                        padding: "0.64285714rem",
                        color: Colors.WHITE,
                        fontSize: "1.71428571rem",
                        background: rightBottomButton.btnColor,
                        textTransform: "capitalize",
                        "&.Mui-disabled": {
                          background: Colors.BACKSHADE_GRAY,
                          color: Colors.ACCENT_GRAY_DISABLED,
                        },
                      }}
                    >
                      {rightBottomButton.label}
                    </CustomButton>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <TopRightButton
            variation="Back"
            onClick={() => navigate(`/pos/${customerId}`)}
          />
        </Box>
        <ManagerCodeModal
          open={isRefundAccessModalOpen}
          onClose={() => setIsRefundAccessModalOpen(false)}
          title="Manager Approval"
          task="refund this invoice"
          onDone={(managerId) => {
            setIsRefundAccessModalOpen(false);
            setRefundApprovedManagerId(managerId);
            _handleOpenRefundInvoiceModal();
          }}
          actionType="REFUND_INVOICE_ACCESS"
          requestedByUserId={user?.id}
          billId={cartHistorySelectedRows[0]?.id}
        />
        <ManagerCodeModal
          open={isEditAccessModalOpen}
          onClose={() => setIsEditAccessModalOpen(false)}
          title="Manager Approval"
          task="edit this invoice"
          onDone={() => {
            setIsEditAccessModalOpen(false);
            _handleEditInvoice();
          }}
          actionType="EDIT_INVOICE_ACCESS"
          requestedByUserId={user?.id}
          billId={cartHistorySelectedRows[0]?.id}
        />
        <ManagerCodeModal
          open={isVoidAccessModalOpen}
          onClose={() => setIsVoidAccessModalOpen(false)}
          title="Manager Approval"
          task="void this invoice"
          onDone={(managerId) => {
            setIsVoidAccessModalOpen(false);
            setVoidApprovedManagerId(managerId);
            _handleOpenVoidInvoiceModal();
          }}
          actionType="VOID_INVOICE_ACCESS"
          requestedByUserId={user?.id}
          billId={cartHistorySelectedRows[0]?.id}
        />
        <PickupModal
          open={isPickupModalOpen}
          onClose={() => {
            setIsPickupModalOpen(false);
            setPartialPickupRequested(false);
          }}
          initialPartialMode={partialPickupRequested}
        />
        <VoidInvoiceModal
          onRefundSuccess={() => handleFetchBillPaymentHistory(() => {})}
          onClose={() => {
            dispatch(CartSliceAction.clearBillPaymentDetails());
            setIsVoidModalOpen(false);
          }}
          open={isVoidModalOpen}
          setVoidTypeIndex={setVoidTypeIndex}
          voidTypeIndex={voidTypeIndex}
          approvedManagerId={voidApprovedManagerId}
        />
        <RefundModal
          open={isReundModalOpen}
          onRefundSuccess={() => handleFetchBillPaymentHistory(() => {})}
          onClose={() => setIsRefundModalOpen(false)}
          approvedManagerId={refundApprovedManagerId}
        />
      </>
    </PosLayout>
  );
};

export default CustomerHistoryPage;
